import {FC, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'

import {InFlight} from './runDriverContext'
import {Run, partName, runProgress, secsLeft} from './types'

/** Seconds as the page shows them, in the user's language: 45 sec, 12 min, 1 hr 5 min. */
export const useDuration = () => {
  const intl = useIntl()
  const unit = (value: number, name: string) =>
    intl.formatNumber(value, {style: 'unit', unit: name, unitDisplay: 'short'})
  return (secs: number) => {
    if (secs < 60) return unit(Math.max(0, Math.floor(secs)), 'second')
    const minutes = Math.floor(secs / 60)
    if (minutes < 60) return unit(minutes, 'minute')
    return `${unit(Math.floor(minutes / 60), 'hour')} ${unit(minutes % 60, 'minute')}`
  }
}

type Props = {
  run: Run
  /** The part this tab has out; one sent from elsewhere shows without its clock. */
  inFlight: InFlight | null
  /** The parts this tab's pass still sends after it. */
  queued: Set<number> | null
  /** One line and a thin bar, for the notice. */
  compact?: boolean
}

/** A run's progress in students, the part being generated, and the time left at the pace so far. */
const RunMeter: FC<Props> = ({run, inFlight, queued, compact = false}) => {
  const intl = useIntl()
  const duration = useDuration()
  const [now, setNow] = useState(Date.now())

  const text = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({id: `FEE.GENERATE_VOUCHERS.${id}`}, values)

  // A clock while a part is out, so a large class is seen to be working.
  useEffect(() => {
    if (!inFlight) return
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [inFlight])

  const progress = runProgress(run, queued)
  const outSecs = inFlight ? Math.max(0, (now - inFlight.sentAt) / 1000) : 0
  const live = run.state === 'running' || run.state === 'interrupted'
  const part = inFlight
    ? run.chunks[inFlight.index]
    : run.chunks.find((chunk) => chunk.status === 'running')

  const leftSecs = live ? secsLeft(progress, outSecs) : null
  let left = ''
  if (leftSecs !== null) {
    left =
      leftSecs < 60
        ? text('PROGRESS.LEFT_SOON')
        : text('PROGRESS.LEFT', {time: duration(Math.ceil(leftSecs / 60) * 60)})
  } else if (inFlight) {
    left = text('PROGRESS.ESTIMATING')
  }

  let spent = ''
  if (inFlight) spent = text('PROGRESS.SPENT', {time: duration(progress.spentSecs + outSecs)})
  else if (run.state === 'completed' && progress.spentSecs > 0)
    spent = text('PROGRESS.TOOK', {time: duration(progress.spentSecs)})

  const students = text('PROGRESS.STUDENTS', {
    done: intl.formatNumber(progress.done),
    total: intl.formatNumber(progress.students),
  })
  const bar = (height: string) => (
    <div className={`progress ${height}`}>
      <div
        className='progress-bar'
        role='progressbar'
        aria-valuenow={progress.percent}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{width: `${progress.percent}%`}}
      />
    </div>
  )

  if (compact) {
    return (
      <>
        <div className='d-flex flex-wrap justify-content-between text-muted fs-7 mt-1 mb-2'>
          <span className='me-3'>{students}</span>
          {left && <span>{left}</span>}
        </div>
        {bar('h-5px')}
      </>
    )
  }

  return (
    <div className='mb-5'>
      <div className='d-flex flex-wrap justify-content-between align-items-baseline mb-2'>
        <span className='fs-4 fw-bold me-5'>{students}</span>
        <span className='text-muted'>{[spent, left].filter(Boolean).join(' · ')}</span>
      </div>
      {bar('h-10px mb-2')}
      {live && part && (
        <div className='text-muted'>
          {text('PROGRESS.NOW', {name: partName(part, text)})}
          {inFlight && ` · ${duration(outSecs)}`}
        </div>
      )}
    </div>
  )
}

export default RunMeter
